import React, { useState } from 'react';
import { Device } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup.pb';
import {
  CreateResponse,
  ReadResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { useQuery } from 'react-query';
import { ForwardingNumberApi, ForwardingNumberTypes } from '@frontend/api-forwarding-number';
import SipProfileAPI from '@frontend/api-sip-profile';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useModalControl, ModalControlResponse, IconProps } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { NewAssignDeviceModal } from '../assign-device-modal/assign-device-modal.new';
import { CallLegs } from './call-legs';

interface Props {
  callGroup: ReadResponse;
  enableDeviceEditing?: boolean;
  tableLayout?: 'combined' | 'separated';
  removeIcon?: React.FC<React.PropsWithChildren<IconProps>>;
  showLocationColumn?: boolean;
  showCombinedView?: boolean;
  callForwardingNumbers?: ForwardingNumberTypes.FwdNumberModel[];
  externalModalProps?: ModalControlResponse;
  onRemoveDevice: (deviceKey: string) => void;
  onRingChange: (deviceKey: string, delayStart: number, timeout: number) => void;
  onDevicesAssigned: (devices: Device[]) => void;
}

export const CallLegsWithModal = ({
  callGroup,
  enableDeviceEditing = false,
  tableLayout = 'combined',
  removeIcon = () => <Icon name='remove2' color='subdued' />,
  showLocationColumn = false,
  callForwardingNumbers: externalCallForwardingNumbers,
  externalModalProps,
  onRemoveDevice,
  onRingChange,
  onDevicesAssigned,
}: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-groups' });
  const internalAssignDeviceModalProps = useModalControl();
  const assignDeviceModalProps = externalModalProps || internalAssignDeviceModalProps;
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { isSingleTypeScope } = useAppScopeStore();

  const { data: devices } = useScopedQuery({
    queryKey: queryKeys.devicesWithAddresses(),
    queryFn: () =>
      SipProfileAPI.List({ tenantId: settingsTenantLocation?.phoneTenantId }).then((res) => res.sipProfiles),
    select: (data) =>
      data?.sort((a, b) => {
        const aName = a.device?.deviceName || '';
        const bName = b.device?.deviceName || '';
        return aName.localeCompare(bName);
      }),
    enabled: !!settingsTenantLocation?.phoneTenantId,
    meta: {
      error: {
        message: 'CallLegsWithModal:devicesWithAddressesQuery | Failed to fetch devices with addresses',
        data: { callGroupId: callGroup.callGroupId, phoneTenantId: settingsTenantLocation?.phoneTenantId },
      },
    },
  });

  const fetchedCallForwardingNumbers = useQuery({
    queryKey: queryKeys.forwardingNumbers(),
    queryFn: () => ForwardingNumberApi.list({ locationId: settingsTenantLocation?.id }),
    select: (forwardingData) =>
      forwardingData.sort((a, b) => {
        const aName = a.name || '';
        const bName = b.name || '';
        return aName.localeCompare(bName);
      }),
    enabled: !externalCallForwardingNumbers,
    meta: {
      error: {
        message: 'CallLegsWithModal:forwardingNumbersQuery | Failed to fetch forwarding numbers',
        data: {
          callGroupId: callGroup.callGroupId,
          phoneTenantId: settingsTenantLocation?.phoneTenantId,
          locationId: settingsTenantLocation?.id,
        },
      },
    },
  });

  const callForwardingNumbers = fetchedCallForwardingNumbers.data;

  const locationsNames =
    settingsTenantLocation && 'children' in settingsTenantLocation && Array.isArray(settingsTenantLocation.children)
      ? settingsTenantLocation.children.reduce((acc: Record<string, string>, item) => {
          if (item.id && item.name) {
            acc[item.id] = item.name;
          }
          return acc;
        }, {} as Record<string, string>)
      : {};

  const preSelection = callGroup.devices.reduce<{
    forwardingNumbers: string[];
    sipProfiles: string[];
  }>(
    (acc, device) => {
      if ('forwardingNumber' in device) {
        acc.forwardingNumbers.push(device.forwardingNumber.forwardingNumberId);
      }
      if ('deskPhone' in device) {
        acc.sipProfiles.push(device.deskPhone.sipProfileId);
      }
      if ('mobilePhone' in device) {
        acc.sipProfiles.push(device.mobilePhone.sipProfileId);
      }
      if ('softPhone' in device) {
        acc.sipProfiles.push(device.softPhone.sipProfileId);
      }
      return acc;
    },
    { forwardingNumbers: [], sipProfiles: [] }
  );

  const [preSelectedSipProfileIDs, setPreSelectedSipProfileIDs] = useState<string[]>(preSelection.sipProfiles ?? []);
  const [preSelectedForwardingNumbers, setPreSelectedForwardingNumbers] = useState<string[]>(
    preSelection.forwardingNumbers ?? []
  );

  return (
    <>
      <CallLegs
        onAssignDevice={() => {
          assignDeviceModalProps.openModal();
        }}
        callGroup={callGroup}
        devices={devices}
        enableDeviceEditing={enableDeviceEditing}
        tableLayout={tableLayout}
        removeIcon={removeIcon}
        showLocationColumn={showLocationColumn}
        onRemoveDevice={onRemoveDevice}
        onRingChange={onRingChange}
      />
      <NewAssignDeviceModal
        tenantLocation={settingsTenantLocation}
        callForwardingNumbers={callForwardingNumbers ?? []}
        callGroup={callGroup as CreateResponse}
        devices={devices || []}
        locationsNames={locationsNames}
        ringtone={callGroup.ringtone}
        modalProps={assignDeviceModalProps.modalProps}
        preSelectedSipProfileIDs={preSelectedSipProfileIDs}
        setPreSelectedSipProfileIDs={setPreSelectedSipProfileIDs}
        preSelectedForwardingNumbers={preSelectedForwardingNumbers}
        setPreSelectedForwardingNumbers={setPreSelectedForwardingNumbers}
        subtitle={t('Assign an existing desk phone, mobile app, or forwarding number to the call group.')}
        title={t('Assign Devices to {{name}}', { name: callGroup.name })}
        isMultiLocation={!isSingleTypeScope}
        onDeferredSave={onDevicesAssigned}
      />
    </>
  );
};
